// @tennis-matches/services/profile/profile-visibility
//
// Konstanten + Helper für die selbstbestimmte Datenfreigabe.
// Quelle der Wahrheit: RPC public.resolve_user_profile / resolve_users_profiles
// (siehe Migration 20260655000354_user_profile_fields_and_visibility.sql).

/**
 * Reihenfolge entspricht der UI (Profil + Hero). Wird sowohl im Web-Client als
 * auch in der MatchMixer-App verwendet.
 *
 * Hinweis: snake_case (RPC) ↔ camelCase (UI) Mapping siehe profile.remote.js.
 */
export const PROFILE_FIELDS = ["firstName", "lastName", "birthDate", "city", "phone"];

export const VISIBILITY_VALUES = ["private", "club"];

/**
 * Defaults für neue Nutzer:innen.
 * first_name / last_name = club (Mitglieder sehen mindestens den Namen).
 * Alles weitere = private (sensiblere Daten erst nach aktiver Freigabe).
 */
export const DEFAULT_VISIBILITY = Object.freeze({
  firstName: "club",
  lastName: "club",
  birthDate: "private",
  city: "private",
  phone: "private",
});

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * Vereinheitlicht eine UI-Visibility-Map auf die erlaubten Werte und befüllt
 * fehlende Felder mit den Defaults. Eingabe darf snake_case oder camelCase sein.
 *
 * @param {object|null|undefined} raw
 * @returns {Record<string, 'private'|'club'>}
 */
export function normalizeVisibilityMap(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const next = { ...DEFAULT_VISIBILITY };
  for (const field of PROFILE_FIELDS) {
    const snake = field.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    const v = __asStr(src[field] ?? src[snake]).toLowerCase();
    if (VISIBILITY_VALUES.includes(v)) {
      next[field] = v;
    }
  }
  return next;
}

/**
 * Konvertiert camelCase-Map in die snake_case-Variante für die RPC.
 *
 * @param {object|null|undefined} mapCamel
 * @returns {Record<string, 'private'|'club'>}
 */
export function visibilityMapToRemote(mapCamel) {
  const norm = normalizeVisibilityMap(mapCamel);
  return {
    first_name: norm.firstName,
    last_name: norm.lastName,
    birth_date: norm.birthDate,
    city: norm.city,
    phone: norm.phone,
  };
}

/**
 * Baut den Anzeigenamen aus einem aufgelösten Profil (RPC-Output).
 * Reihenfolge:
 *   1) Wenn first/last_name sichtbar sind → "Vorname Nachname".
 *   2) Wenn nur first sichtbar → "Vorname".
 *   3) Wenn nur last sichtbar + display_name vorhanden → "Vorname-Initial Nachname"
 *      (Vorname-Initial nur, wenn im display_name als erstes Wort enthalten).
 *   4) Fallback: display_name aus dem RPC.
 *   5) Letzter Fallback: options.fallback bzw. leerer String.
 *
 * @param {object|null|undefined} profile RPC-Resolve-Output (snake_case).
 * @param {{ fallback?: string, abbreviateLastName?: boolean }} [options]
 * @returns {string}
 */
export function formatVisibleDisplayName(profile, options = {}) {
  const fallback = __asStr(options?.fallback);
  if (!profile || typeof profile !== "object") return fallback;

  const first = __asStr(profile.first_name ?? profile.firstName);
  const last = __asStr(profile.last_name ?? profile.lastName);
  const display = __asStr(profile.display_name ?? profile.displayName);

  if (first && last) {
    if (options.abbreviateLastName) {
      return `${first} ${last.charAt(0)}.`;
    }
    return `${first} ${last}`;
  }
  if (first && !last && display) {
    // Wir wissen nur den Vornamen → fallback "Vorname X." aus display_name.
    const tail = display.split(/\s+/).slice(1).join(" ").trim();
    if (tail) {
      return `${first} ${tail.charAt(0)}.`;
    }
    return first;
  }
  if (first) return first;
  if (last) return last;
  if (display) return display;
  return fallback;
}
